import React, { useCallback, useState, useMemo, useEffect } from "react"
import { Outlet, useNavigate } from "react-router-dom"
import { routes } from "router/routes"
import { Button, Col, Dropdown, Flex, Input, Row, Grid } from "antd"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import CustomPagination from "components/common/CustomPagination"
import { debounce } from "lodash"
import { useTitle } from "hooks/useTitle"
import { useTranslation } from "react-i18next"
import { CloseCircleOutlined, DownOutlined, FilterFilled, PlusOutlined, SearchOutlined } from "@ant-design/icons"
import styles from "./clients.module.css"

// Redux
import { useDispatch, useSelector } from "react-redux"
import { getGuideClients, cleanClients, cleanClient } from "store/clients"
import { getGroups } from "store/groups"
import { collectionAnySelector } from "store/selectors"
import { VERIFICATION_STATUS } from "layoutsV2/pro/PersonalInfoLayout/constants"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import ListingCard from "./ListingCard"
import { EllipsisIcon } from "icons"
import ClientTable from "./ClientTable"
import LoadMoreButton from "components/common/LoadMore"
import FilterSortDrawer from "./Drawers/FilterSortDrawer"
import ActionDrawers from "./Drawers/ActionDrawer"
import { Search } from "ui/Icon/DSIcon/Search"
import { UserAvatar } from "ui/Icon/DSIcon/UserAvatar"
import useActiveGuide from "hooks/useActiveClient"

const DELAY_TIME = 300

export default function Clients() {
  useTitle("Clients")
  useLoaderWatchers({ watchers: ["clients", "clients.groupClients"] })
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchBy, setSearchBy] = useState("")
  const { guide } = useSelector((state) => state.user)
  const status = guide?.verification_status
  const isPending = status === VERIFICATION_STATUS.PENDING
  const clientsState = useSelector((state) => state.clients)
  const clients = clientsState.items
  const metadata = clientsState.metadata
  const hasClients = useSelector(collectionAnySelector("clients"))
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortField, setSortField] = useState(null)
  const [sortOrder, setSortOrder] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState("")
  const [sortBy, setSortBy] = useState("nameAsc")
  const [actionsDrawerOpen, setActionsDrawerOpen] = useState(false)
  const [filterSortDrawerOpen, setFilterSortDrawerOpen] = useState(false)
  const [selectedSortLabel, setSelectedSortLabel] = useState(t("clients.sort_by"))
  const [selectedGroups, setSelectedGroups] = useState([])
  const [groupAction, setGroupAction] = useState("")
  const [selectedClientIds, setSelectedClientIds] = useState([])
  const [removeGroups, setRemoveGroups] = useState([])
  const [isMenuListHide, setIsMenuListHide] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [hasDeleteClient, setHasDeleteClient] = useState(false)
  const [clientId, setClientId] = useState(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const { useBreakpoint } = Grid
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const [active] = useActiveGuide()

  // Get groups from Redux store with memoization
  const groups = useSelector((state) => state.groups?.items || [])
  const groupsLoading = useSelector((state) => state.groups?.loading)

  // Fetch groups on component mount
  useEffect(() => {
    dispatch(getGroups())

    return () => {
      dispatch(cleanClients())
    }
  }, [dispatch])

  // Initial load
  useEffect(() => {
    fetchClients()
    return () => {
      dispatch(cleanClients())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchFieldHandler = ({ target }) => {
    const { value } = target || {}
    // Use original value for UI input
    setSearchBy(value)
    // Check if trimmed value has any content before searching
    const trimmedValue = value ? value.trim() : ""
    if (trimmedValue.length > 0) {
      // Always reset to page 1 on new search
      debouncedSearch(trimmedValue)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      // Always pass current sortField and sortOrder, and reset to page 1
      fetchClients(value, selectedGroup, false, 1, sortField, sortOrder)
    }, DELAY_TIME),
    [sortField, sortOrder, selectedGroup]
  )

  const clearSearchInput = () => {
    // First clear the search input
    setSearchBy("")
    // Then trigger the search with an empty string to clear the search
    // This will be handled in the fetchClients function
    fetchClients("")
    setSelectedRowKeys([])
  }

  // Memoize the fetchClients function to prevent recreation
  const fetchClients = useCallback(
    (searchTerm, groupId, append, page, sortFieldOverride, sortOrderOverride, pageSizeOverride) => {
      const _groupId = groupId === undefined ? selectedGroup : groupId
      const _append = append === undefined ? false : append
      const _page = page === undefined ? currentPage : page

      const queryParams = {}

      const searchValue = searchTerm === undefined ? searchBy : searchTerm
      if (searchValue) {
        queryParams["guide_client_search"] = searchValue
      }

      if (_groupId && _groupId !== "all") {
        queryParams["groups_id_in"] = _groupId
      }

      // Use override if provided, else fallback to state
      const effectiveSortField = sortFieldOverride || sortField
      const effectiveSortOrder = sortOrderOverride || sortOrder
      if (effectiveSortField) {
        queryParams[effectiveSortField] = effectiveSortOrder === "ascend" ? "asc" : "desc"
      }

      // For mobile append mode, we want to fetch the next page
      // For regular pagination, use the provided page
      const pageToFetch = _append ? currentPage + 1 : _page

      if (!_append) {
        setCurrentPage(pageToFetch)
      }

      // Always use the correct page size (override or state)
      const perPage = pageSizeOverride || pageSize
      return dispatch(
        getGuideClients(
          pageToFetch,
          {
            q: queryParams,
            per_page: perPage
          },
          _append
        )
      ).then(() => {
        if (_append) {
          setCurrentPage(pageToFetch)
        }
      })
    },
    [searchBy, selectedGroup, sortField, sortOrder, currentPage, pageSize, dispatch]
  )

  // Handle group selection
  const handleGroupSelect = useCallback(
    (group) => {
      setSelectedGroup(group)
      setCurrentPage(1)
      fetchClients(searchBy, group === "all" ? null : group, false, 1)
    },
    [searchBy, fetchClients]
  )

  const handleLoadMore = useCallback(() => {
    fetchClients(searchBy, selectedGroup, true)
  }, [fetchClients, searchBy, selectedGroup])

  // Memoize group menu items
  const groupMenuItems = useMemo(
    () => ({
      items: [
        {
          key: "all",
          label: t("clients.all_groups"),
          onClick: () => handleGroupSelect("all")
        },
        ...groups.map((group) => ({
          key: String(group.id),
          label: group.name,
          onClick: () => handleGroupSelect(String(group.id))
        }))
      ]
    }),
    [groups, t, handleGroupSelect]
  )

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter?.column) {
      const sortFieldMap = {
        name: "sort_by_user_full_name",
        group: "sort_by_group_name",
        experience_date: "sort_by_last_trip_date",
        ytd_spend: "sort_by_total_booking_amount"
      }

      const fieldKey = sorter.field
      const sortParam = sortFieldMap[fieldKey] || fieldKey

      let nextSortField = sortField
      let nextSortOrder

      if (sortField === sortParam) {
        if (sortOrder === "descend") {
          nextSortOrder = "ascend"
        } else {
          nextSortOrder = "descend"
        }
      } else {
        nextSortField = sortParam
        nextSortOrder = "ascend"
      }

      setSortField(nextSortField)
      setSortOrder(nextSortOrder)

      // Call fetchClients with the new sorting params
      fetchClients(searchBy, selectedGroup, false, 1, nextSortField, nextSortOrder)
    } else {
      const defaultSortField = "sort_by_last_trip_date"
      const defaultSortOrder = "descend"
      setSortField(defaultSortField)
      setSortOrder(defaultSortOrder)
      fetchClients(searchBy, selectedGroup, false, 1, defaultSortField, defaultSortOrder)
    }
  }

  const handlePageChange = (page) => {
    // Don't set currentPage here, let fetchClients handle it
    fetchClients(searchBy, selectedGroup, false, page)
  }

  const handlePageSizeChange = (size) => {
    setPageSize(size)
    setCurrentPage(1)
    // Pass size as explicit param to fetchClients
    fetchClients(searchBy, selectedGroup, false, 1, sortField, sortOrder, size)
  }

  const handleShowDrawer = () => {
    setActionsDrawerOpen(true)
  }

  const getInitialSortLabel = useCallback(() => {
    if (!sortField) return t("clients.sort_by")

    if (sortField === "sort_by_user_full_name") {
      return sortOrder === "ascend" ? t("clients.sort_by_name_asc") : t("clients.sort_by_name_desc")
    } else if (sortField === "sort_by_total_booking_amount") {
      return sortOrder === "descend" ? t("clients.sort_by_ytd_spent_asc") : t("clients.sort_by_ytd_spent_desc")
    }
    return t("clients.sort_by")
  }, [sortField, sortOrder, t])

  // Initialize selectedSortLabel
  useEffect(() => {
    setSelectedSortLabel(getInitialSortLabel())
  }, [getInitialSortLabel])

  const handleAddClient = () => {
    dispatch(cleanClient()).then(() => navigate(routes.guideClientNewPath()))
  }

  const handleImportClients = () => {
    navigate(routes.guideImportClientsListPath())
  }
  const handleFailedClients = () => {
    navigate(routes.guideImportClientHistory())
  }

  const handleManageGroupsClick = () => {
    navigate(routes.guideClientManageGroup())
  }

  const handleFetchClients = () => {
    fetchClients(searchBy, selectedGroup, false, 1, sortField, sortOrder, pageSize)
  }

  const handleActionClick = (actionType) => {
    if (actionType === "manage_groups") {
      setActionsDrawerOpen(false)
    }
  }

  const handleSelectedGroupsChange = (groups) => {
    // Map for quick lookup by group_id
    const prevMap = new Map(selectedGroups.map((g) => [g.group_id, g]))
    const nextMap = new Map(groups.map((g) => [g.group_id, g]))
    const removeMap = new Map(removeGroups.map((g) => [g.group_id, g]))

    // Groups removed: in prevMap but not in nextMap
    const removed = Array.from(prevMap.values()).filter((g) => !nextMap.has(g.group_id))
    // Groups re-added: in nextMap and in removeMap
    const addedBack = Array.from(nextMap.values()).filter((g) => removeMap.has(g.group_id))

    // Add newly removed groups to removeGroups if not already present
    let newRemoveGroups = [...removeGroups]
    for (const g of removed) {
      if (!removeMap.has(g.group_id)) {
        newRemoveGroups.push(g)
      }
    }

    // Remove groups that were added back from removeGroups
    if (addedBack.length > 0) {
      const addedBackIds = new Set(addedBack.map((g) => g.group_id))
      newRemoveGroups = newRemoveGroups.filter((g) => !addedBackIds.has(g.group_id))
    }

    setSelectedGroups(groups)
    setRemoveGroups(newRemoveGroups)
  }

  let fallbackButtonClickHandler
  if (searchBy) {
    fallbackButtonClickHandler = clearSearchInput
  } else if (selectedGroup) {
    fallbackButtonClickHandler = undefined
  } else {
    fallbackButtonClickHandler = handleAddClient
  }

  return (
    <div className="vstack flex-fill z-0" id="clients">
      <FirstLoading name="clients">
        <div className="flex-fill">
          <div>
            <Flex justify="space-between" wrap="wrap" gap={10} className={`mb-xl ${styles.filterWrap}`}>
              <Flex gap={16} align="center">
                <Input
                  id="search"
                  type="text"
                  placeholder={t("clients.search_clients")}
                  value={searchBy}
                  onChange={searchFieldHandler}
                  onKeyDown={(e) => {
                    // Prevent entering space as the first character
                    if (e.key === " " && (!searchBy || searchBy.length === 0)) {
                      e.preventDefault()
                    }
                  }}
                  className={styles.searchInput}
                  prefix={<SearchOutlined aria-hidden="true" />}
                  suffix={searchBy && <CloseCircleOutlined className={styles.clearSearchIcon} onClick={clearSearchInput} />}
                  aria-label={t("clients.search_clients")}
                  aria-controls="clients-search-results"
                  aria-expanded={!!searchBy}
                  aria-autocomplete="list"
                  aria-activedescendant={searchBy ? "clients-search-results" : undefined}
                />
                <Dropdown menu={groupMenuItems} className={styles.hideFromMobile} trigger={["click"]}>
                  <Button
                    className={styles.dropdownButton}
                    size="large"
                    aria-label={t("clients.all_groups")}
                    loading={groupsLoading}
                    disabled={!active}
                    aria-disabled={!active}
                  >
                    {selectedGroup === "all"
                      ? t("clients.all_groups")
                      : groups.find((g) => String(g.id) === selectedGroup)?.name || t("clients.all_groups")}
                    <DownOutlined />
                  </Button>
                </Dropdown>
                <Dropdown
                  menu={{
                    items: [
                      { key: "nameAsc", label: t("clients.sort_by_name_asc") },
                      { key: "nameDesc", label: t("clients.sort_by_name_desc") },
                      { key: "ytdHigh", label: t("clients.sort_by_ytd_spent_asc") },
                      { key: "ytdLow", label: t("clients.sort_by_ytd_spent_desc") }
                    ],
                    onClick: ({ key }) => {
                      let nextSortField, nextSortOrder
                      setSortBy(key)
                      if (key === "nameAsc") {
                        nextSortField = "sort_by_user_full_name"
                        nextSortOrder = "ascend"
                      } else if (key === "nameDesc") {
                        nextSortField = "sort_by_user_full_name"
                        nextSortOrder = "descend"
                      } else if (key === "ytdHigh") {
                        nextSortField = "sort_by_total_booking_amount"
                        nextSortOrder = "descend"
                      } else if (key === "ytdLow") {
                        nextSortField = "sort_by_total_booking_amount"
                        nextSortOrder = "ascend"
                      }
                      setSortField(nextSortField)
                      setSortOrder(nextSortOrder)
                      fetchClients(searchBy, selectedGroup, false, 1, nextSortField, nextSortOrder)
                    }
                  }}
                  className={styles.hideFromMobile}
                  trigger={["click"]}
                >
                  <Button
                    className={`${styles.dropdownButton} ${styles.dropdownButtonTablet}`}
                    size="large"
                    aria-label={t("clients.sort_by")}
                  >
                    {selectedSortLabel} <DownOutlined />
                  </Button>
                </Dropdown>
                <Button
                  shape="circle"
                  className={`${styles.mobileOnly} ${styles.mobileIconButton}`}
                  onClick={() => setFilterSortDrawerOpen(true)}
                >
                  <FilterFilled className={` cursor-pointer`} width={15} height={15} />
                </Button>
                {filterSortDrawerOpen && (
                  <FilterSortDrawer
                    open={filterSortDrawerOpen}
                    setOpen={setFilterSortDrawerOpen}
                    setSortField={setSortField}
                    setSortOrder={setSortOrder}
                    selectedGroup={selectedGroup}
                    setSelectedGroup={setSelectedGroup}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    groups={groups} // Pass groups from parent
                    fetchClients={fetchClients}
                    searchBy={searchBy}
                  />
                )}
              </Flex>
              <Flex gap={16} align="center" className={`ml-auto ${styles.filterActions}`}>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  aria-label="Failed Import Report"
                  onClick={active ? handleFailedClients : undefined}
                  disabled={!active}
                  className="flex-1"
                  aria-disabled={!active}
                >
                  Import History
                </Button>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  aria-label={t("clients.import_client_list")}
                  onClick={active ? handleImportClients : undefined}
                  disabled={!active}
                  className="flex-1"
                  aria-disabled={!active}
                >
                  {t("clients.import_client_list")}
                </Button>
                <Button
                  className={styles.hideFromMobile}
                  color="primary"
                  variant="outlined"
                  size="large"
                  aria-label={t("clients.manage_groups")}
                  onClick={active && !isPending ? handleManageGroupsClick : undefined}
                  disabled={!active || isPending}
                  aria-disabled={!active || isPending}
                >
                  {t("clients.manage_groups")}
                </Button>
                <Button
                  type="primary"
                  onClick={active && !isPending ? handleAddClient : undefined}
                  aria-label={t("clients.add_client")}
                  size="large"
                  icon={<PlusOutlined aria-hidden="true" />}
                  className={styles.addClientButton}
                  disabled={!active || isPending}
                  aria-disabled={!active || isPending}
                >
                  {t("clients.add_client")}
                </Button>
                <Button shape="circle" className={`${styles.mobileOnly} ${styles.mobileIconButton}`} onClick={handleShowDrawer}>
                  <EllipsisIcon className={`cursor-pointer`} fill="#525455" width={15} height={15} />
                </Button>
                {actionsDrawerOpen && (
                  <ActionDrawers
                    actionType="moreActions"
                    open={actionsDrawerOpen}
                    setOpen={setActionsDrawerOpen}
                    handleActionClick={handleActionClick}
                  />
                )}
              </Flex>
            </Flex>
            {!clientsState.loading && searchBy && (
              <div className="mb-m">
                <Text color={colors.textSecondaryDark}>{t("clients.client_found", { count: metadata?.total_results })}</Text>
              </div>
            )}
            <div className={`commonGrid`}>
              <WithEmptyStateWrapper
                name="client"
                hasItems={hasClients}
                fallbackImageSrc={searchBy || selectedGroup ? <Search /> : <UserAvatar />}
                fallbackTitle={searchBy || selectedGroup ? t("clients.no_exact_match") : null}
                fallbackButtonText={searchBy ? t("clients.clear_search") : null}
                fallbackButtonClickHandler={fallbackButtonClickHandler}
                fallback={clientsState.loading && <></>}
                isPending={isPending}
              >
                <Row gutter={[0, 16]}>
                  <Col xs={24} md={0}>
                    <Flex vertical gap={15}>
                      <ListingCard
                        clients={clients}
                        selectedGroups={selectedGroups}
                        setSelectedGroups={setSelectedGroups}
                        groupAction={groupAction}
                        setGroupAction={setGroupAction}
                        selectedClientIds={selectedClientIds}
                        setSelectedClientIds={setSelectedClientIds}
                        removeGroups={removeGroups}
                        setRemoveGroups={setRemoveGroups}
                        isMenuListHide={isMenuListHide}
                        setIsMenuListHide={setIsMenuListHide}
                        handleFetchClients={handleFetchClients}
                        selectedClient={selectedClient}
                        setSelectedClient={setSelectedClient}
                        handleSelectedGroupsChange={handleSelectedGroupsChange}
                        hasDeleteClient={hasDeleteClient}
                        setHasDeleteClient={setHasDeleteClient}
                        clientId={clientId}
                        setClientId={setClientId}
                      />
                    </Flex>
                  </Col>
                  <Col xs={0} md={24}>
                    <ClientTable
                      clients={clients}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleTableChange={handleTableChange}
                      handleFetchClients={handleFetchClients}
                      selectedGroups={selectedGroups}
                      setSelectedGroups={setSelectedGroups}
                      groupAction={groupAction}
                      setGroupAction={setGroupAction}
                      selectedClientIds={selectedClientIds}
                      setSelectedClientIds={setSelectedClientIds}
                      removeGroups={removeGroups}
                      setRemoveGroups={setRemoveGroups}
                      isMenuListHide={isMenuListHide}
                      setIsMenuListHide={setIsMenuListHide}
                      selectedClient={selectedClient}
                      setSelectedClient={setSelectedClient}
                      handleSelectedGroupsChange={handleSelectedGroupsChange}
                      hasDeleteClient={hasDeleteClient}
                      setHasDeleteClient={setHasDeleteClient}
                      clientId={clientId}
                      setClientId={setClientId}
                      selectedRowKeys={selectedRowKeys}
                      setSelectedRowKeys={setSelectedRowKeys}
                    />
                  </Col>
                </Row>
                {metadata?.total_results > 10 && (
                  <div className="mb-m">
                    {isMobile ? (
                      <LoadMoreButton
                        currentPage={currentPage}
                        totalPages={Math.ceil(metadata.total_results / pageSize)}
                        loading={clientsState.loading}
                        onLoadMore={handleLoadMore}
                        totalAvailable={metadata.total_results}
                      />
                    ) : (
                      <CustomPagination
                        metadata={metadata}
                        pageSize={pageSize}
                        total={metadata?.total_results || 0}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                      />
                    )}
                  </div>
                )}
              </WithEmptyStateWrapper>
            </div>
          </div>
        </div>
        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </FirstLoading>
    </div>
  )
}
